import type {
    ButtonHTMLAttributes,
    CSSProperties,
    FC,
    ReactNode
} from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import RollText from '@/components/motion/roll-text'

type ButtonVariant = 'acid' | 'ghost' | 'ink' | 'bone' | 'ghost-dark'

interface ButtonProps {
    children: string
    href?: string
    variant?: ButtonVariant
    icon?: ReactNode | false
    className?: string
    type?: ButtonHTMLAttributes<HTMLButtonElement>['type']
    disabled?: boolean
    onClick?: () => void
    transitionLabel?: string
    external?: boolean
    ariaLabel?: string
}

const VARIANTS: Record<ButtonVariant, { className: string, wipe: string }> = {
    acid: { className: 'bg-acid text-ink hover:text-acid', wipe: 'var(--ink)' },
    ghost: { className: 'border border-line text-bone hover:text-ink hover:border-bone', wipe: 'var(--bone)' },
    'ghost-dark': { className: 'border border-ink/25 text-ink hover:text-bone hover:border-ink', wipe: 'var(--ink)' },
    ink: { className: 'bg-ink text-bone hover:text-ink', wipe: 'var(--acid)' },
    bone: { className: 'bg-bone text-ink hover:text-ink', wipe: 'var(--acid)' }
}

/** Buttons and button-styled links: fill wipe plus letter roll on hover. */
const Button: FC<ButtonProps> = ({
    children,
    href,
    variant = 'acid',
    icon,
    className,
    type = 'button',
    disabled = false,
    onClick,
    transitionLabel,
    external = false,
    ariaLabel
}): ReactNode => {
    const style = VARIANTS[variant]
    const classes = cn(
        'wipe roll-host group/button inline-flex h-12 items-center justify-center gap-3 px-6 label-mono transition-colors duration-500 ease-expo-out disabled:pointer-events-none disabled:opacity-50',
        style.className,
        className
    )

    const content = (
        <>
            <RollText text={children} />

            {icon !== false && (
                <span aria-hidden='true' className='inline-flex overflow-hidden'>
                    {icon ?? <ArrowUpRight className='size-4 transition-transform duration-500 ease-expo-out group-hover/button:rotate-45' />}
                </span>
            )}
        </>
    )

    const wipeStyle = { '--wipe-color': style.wipe } as CSSProperties

    if (href && external) {
        return (
            <a href={href} className={classes} style={wipeStyle} aria-label={ariaLabel} target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noreferrer' : undefined}>
                {content}
            </a>
        )
    }

    if (href) {
        return (
            <Link href={href} className={classes} style={wipeStyle} aria-label={ariaLabel} data-transition-label={transitionLabel}>
                {content}
            </Link>
        )
    }

    return (
        <button type={type} className={classes} style={wipeStyle} disabled={disabled} onClick={onClick} aria-label={ariaLabel}>
            {content}
        </button>
    )
}

export default Button
